// src/components/map/hooks/adapters/useMapWellHighlighting.ts
//
// Adapter hook: Well Highlighting
//
// Owns:
//   - getWellLayerLineThickness() (base line-width computation)
//   - getHighlightedLineThickness() (highlighted line-width computation)
//   - the series highlight: hovered ∪ selected wells redrawn on the highlight
//     layers above every well, the well layers dimmed underneath
//   - facility icon-size highlighting
//
// Mapbox methods called: setFilter, setPaintProperty, setLayoutProperty
//
// Store reads: selectDisplaySettings (wellLineThickness), Wells layer visibility
//
// This hook was extracted from MapboxWrapper as part of the concern-based
// decomposition (Phase 6).
import * as Sentry from "@sentry/react";
import { MutableRefObject, useCallback, useEffect, useRef } from "react";

import { SERIES_HIGHLIGHT_DELAY_MS } from "atoms/interaction";
import {
  FACILITY_LAYER,
  MAP_BLUR_OPACITY,
  MAX_WELLS_TO_HIGHLIGHT,
  STICK_LAYER,
  WELL_LAYER,
  WELL_LAYER_HIGHLIGHTED,
  WELL_LAYER_POINT,
  WELL_LAYER_POINT_HIGHLIGHTED,
  highlightedWellLineThickness
} from "constants/mapLayers.constants";
import mapboxgl, { Expression } from "mapbox-gl";
import { selectDisplaySettings, selectLayerIsVisibleById, useMapStore } from "stores/map";

import {
  EntitiesInGroupModel,
  HighlightedFeatures
} from "../../contexts/MapContextState";
import { defaultWellLineThickness } from "../../hooks/getMapLayers";
import { hollowDotColorPaint } from "../../utils/baseWellGlyphs";
import { buildHighlightIds } from "../useHighlightSelection";

const selectWellsVisible = selectLayerIsVisibleById(WELL_LAYER);

// Fallback for when the highlight sources load nothing (e.g. below their zoom range).
const HIGHLIGHT_DRAW_TIMEOUT_MS = 1000;

// The literal can hold MAX_WELLS_TO_HIGHLIGHT UWIs; skip the style-spec walk of it.
const NO_VALIDATE = { validate: false };

/** Redraws `colors` (UWI → color) on the highlight layers. */
function setHighlightedWells(mapbox: mapboxgl.Map, colors: Record<string, string>) {
  const isHighlighted = ["has", ["get", "Uwi"], ["literal", colors]];
  // A hovered series is one color: a constant spares Mapbox copying the literal again.
  const uniqueColors = new Set(Object.values(colors));
  const color =
    uniqueColors.size === 1
      ? [...uniqueColors][0]
      : ["get", ["get", "Uwi"], ["literal", colors]];
  // Each follows its well layer's drawn visibility, not just the Wells checkbox:
  // the timeline hides Wells directly during playback.
  const layers: [string, string, unknown[], mapboxgl.LinePaint | mapboxgl.CirclePaint][] =
    [
      [
        WELL_LAYER_HIGHLIGHTED,
        WELL_LAYER,
        isHighlighted,
        { "line-color": color as mapboxgl.Expression | string }
      ],
      [
        WELL_LAYER_POINT_HIGHLIGHTED,
        WELL_LAYER_POINT,
        ["all", isHighlighted, ["!=", ["get", "group"], "leg"]],
        // Licence/permit dots stay hollow when highlighted.
        hollowDotColorPaint(color as mapboxgl.Expression | string)
      ]
    ];
  for (const [id, wellLayer, filter, paint] of layers) {
    if (!mapbox.getLayer(id)) continue;
    mapbox.setFilter(id, filter, NO_VALIDATE);
    for (const [property, value] of Object.entries(paint)) {
      mapbox.setPaintProperty(id, property, value, NO_VALIDATE);
    }
    const isWellLayerShown =
      !!mapbox.getLayer(wellLayer) &&
      mapbox.getLayoutProperty(wellLayer, "visibility") !== "none";
    mapbox.setLayoutProperty(id, "visibility", isWellLayerShown ? "visible" : "none");
  }
}

function setWellsDimmed(mapbox: mapboxgl.Map, dimmed: boolean) {
  const opacity = dimmed ? MAP_BLUR_OPACITY : 1;
  for (const id of [WELL_LAYER, STICK_LAYER]) {
    if (mapbox.getLayer(id)) mapbox.setPaintProperty(id, "line-opacity", opacity);
  }
  if (mapbox.getLayer(WELL_LAYER_POINT)) {
    mapbox.setPaintProperty(WELL_LAYER_POINT, "circle-opacity", opacity);
    // Hollow permit dots draw only a stroke, which circle-opacity doesn't dim.
    mapbox.setPaintProperty(WELL_LAYER_POINT, "circle-stroke-opacity", opacity);
  }
}

// Calls `done` once the well sources, which the highlight layers share, have
// drawn the highlight, so the highlighted series never shows dimmed while they
// reparse. Returns a cancel.
function onHighlightDrawn(mapbox: mapboxgl.Map, done: () => void): () => void {
  const sources = [WELL_LAYER, WELL_LAYER_POINT].filter((id) => mapbox.getSource(id));
  const cancel = () => {
    clearTimeout(timer);
    mapbox.off("sourcedata", onSourceData);
    mapbox.off("idle", finish);
  };
  const finish = () => {
    cancel();
    done();
  };
  const onSourceData = (e: mapboxgl.MapSourceDataEvent) => {
    // Only a well tile event: other sources' events can arrive before these
    // sources have even started reloading.
    if (
      e.tile &&
      sources.includes(e.sourceId) &&
      sources.every((id) => mapbox.isSourceLoaded(id))
    ) {
      finish();
    }
  };
  const timer = setTimeout(finish, HIGHLIGHT_DRAW_TIMEOUT_MS);
  mapbox.on("sourcedata", onSourceData);
  // A hover changes a layer filter, which the sources answer from tiles they
  // already hold: no reload starts, so no tile event ever arrives and the wait
  // ran to the timeout with the highlight sitting over wells at full opacity.
  // `idle` is the same signal without the reload - the map has drawn and has
  // nothing left loading - so it lands on the frame after the highlight when
  // there is nothing to reparse, and after the tiles when there is.
  mapbox.on("idle", finish);
  return cancel;
}

// ── Types ────────────────────────────────────────────────────────────────────

export interface UseMapWellHighlightingParams {
  /** Ref to the Mapbox GL map instance. */
  readonly mapboxRef: MutableRefObject<Maybe<mapboxgl.Map>>;
  /** Build facility icon-size expression that enlarges UWIs present in `data`. From useMapFacilityHighlighting. */
  readonly getFacilityHighlightIconSizeExpression: (
    data: Record<string, number>
  ) => Expression;
  /** Build the base facility icon-size expression. From useMapFacilityHighlighting. */
  readonly getFacilityIconSizeExpression: () => Expression;
  /** The current highlighted features from MapContext. */
  readonly highlightedFeatures: HighlightedFeatures;
  /** Selected group titles; their wells stay highlighted while others dim. */
  readonly selectedGroups: Maybe<string[]>;
  readonly entitiesInGroup: Maybe<{ [title: string]: EntitiesInGroupModel }>;
  /** Flips true after a basemap switch re-adds the well layers. */
  readonly isMapInitialized: boolean;
}

export interface UseMapWellHighlightingReturn {
  /** Get the current base well line thickness from display settings. */
  readonly getWellLayerLineThickness: () => number;
  /** Get the highlighted (enlarged) well line thickness. */
  readonly getHighlightedLineThickness: () => number;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useMapWellHighlighting({
  mapboxRef,
  getFacilityHighlightIconSizeExpression,
  getFacilityIconSizeExpression,
  highlightedFeatures,
  selectedGroups,
  entitiesInGroup,
  isMapInitialized
}: UseMapWellHighlightingParams): UseMapWellHighlightingReturn {
  // ── Zustand selectors ──────────────────────────────────────────────────
  const displaySettings = useMapStore(selectDisplaySettings);

  // ── Line thickness computations ────────────────────────────────────────

  const getWellLayerLineThickness = useCallback((): number => {
    return displaySettings.wellLineThickness || defaultWellLineThickness;
  }, [displaySettings.wellLineThickness]);

  const getHighlightedLineThickness = useCallback(
    (): number => highlightedWellLineThickness(getWellLayerLineThickness()),
    [getWellLayerLineThickness]
  );

  const wellsVisible = useMapStore(selectWellsVisible);
  const lastEntitiesRef = useRef(entitiesInGroup);

  // ── highlight effect ───────────────────────────────────────────────────

  useEffect(() => {
    // Same trailing delay as the charts (useChartSelections) so both change together.
    // Not after a refilter: its wells are already drawn and would show undimmed for the delay.
    const delay =
      entitiesInGroup === lastEntitiesRef.current ? SERIES_HIGHLIGHT_DELAY_MS : 0;
    lastEntitiesRef.current = entitiesInGroup;

    let cancelDim: Maybe<() => void>;
    const timer = setTimeout(() => {
      const mapbox = mapboxRef?.current;
      if (!mapbox?.getLayer(WELL_LAYER)) {
        return;
      }

      // `uwis` is set only for well hovers; facility hover ids are not well UWIs.
      const colors = {
        ...buildHighlightIds(selectedGroups, entitiesInGroup),
        ...highlightedFeatures?.uwis
      };
      const count = Object.keys(colors).length;
      const isActive = count > 0 && count <= MAX_WELLS_TO_HIGHLIGHT;

      try {
        setHighlightedWells(mapbox, isActive ? colors : {});
        const isDimmed =
          mapbox.getPaintProperty(WELL_LAYER, "line-opacity") === MAP_BLUR_OPACITY;
        if (isActive && !isDimmed) {
          cancelDim = onHighlightDrawn(mapbox, () => setWellsDimmed(mapbox, true));
        } else {
          setWellsDimmed(mapbox, isActive);
        }

        const facility = highlightedFeatures?.facility ?? [];
        if (mapbox.getLayer(FACILITY_LAYER)) {
          const facilityIds: Record<string, number> = {};
          for (const feat of facility) facilityIds[feat.properties?.Uwi] = 2.0;
          mapbox.setLayoutProperty(
            FACILITY_LAYER,
            "icon-size",
            facility.length
              ? getFacilityHighlightIconSizeExpression(facilityIds)
              : getFacilityIconSizeExpression()
          );
        }
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setContext("well_highlight", {
            action: "setHighlightedWells",
            status: "failed"
          });
          scope.setLevel("error");
          Sentry.captureException(err);
        });
      }
    }, delay);
    return () => {
      clearTimeout(timer);
      cancelDim?.();
    };
  }, [
    highlightedFeatures,
    mapboxRef,
    selectedGroups,
    entitiesInGroup,
    isMapInitialized,
    // Re-run when the Wells checkbox flips the well layers' visibility.
    wellsVisible,
    getFacilityHighlightIconSizeExpression,
    getFacilityIconSizeExpression
  ]);

  return {
    getWellLayerLineThickness,
    getHighlightedLineThickness
  };
}
